import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faArrowRight, faCheckCircle, faClipboardCheck, faClock, faDolly, faInfoCircle, faMapMarkerAlt, faPencil, faSpinner, faThermometerHalf, faTruckLoading, faCloudXmark, faSync, fas } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionButton, HeadingItem } from 'components'
import { RecapPopup } from 'components/pop-up'
import { TripContext } from 'contexts'
import dateFormat from "dateformat"
import notify from 'devextreme/ui/notify'
import React, { FC, Fragment, lazy, ReactNode, Suspense, useContext, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { useLoadManagerService, useNavigation, useParamService } from 'services'
import { OrderType, StopStatus, Temperature } from 'services/enums'
import { SyncingStatus } from 'services/enums/syncingStatus'
import { Company, Order, Stop, SyncStatus } from 'services/types'
import { IconOptions } from 'types'
import './appointment-card.scss'

type Props = {
    stopId?: number | undefined
    stop?: Stop
    isReadOnly?: boolean
}

export const AppointmentCard: FC<Props> = ({ stopId, stop, isReadOnly = false }) => {

    const AppointmentInfo = lazy(() =>
        import('components/load-appointments')
            .then(({ AppointmentInfo }) => ({ default: AppointmentInfo }))
    )

    const AppointmentSummary = lazy(() =>
        import('components/load-appointments')
            .then(({ AppointmentSummary }) => ({ default: AppointmentSummary }))
    )

    const [detailVisible, setDetailVisible] = useState(false)
    const [moredetailsVisible, setMoreDetailsVisible] = useState(false)
    const [isRecapPopupVisible, setIsRecapPopupVisible] = useState<boolean>(false)
    const { navigateTo } = useNavigation()
    const { syncRequests } = useLoadManagerService()
    const { getIntValue } = useParamService
    let cardContentRef = useRef(null)
    let cardDetailsRef = useRef(null)

    const tripContext = useContext(TripContext)
    const stopData: Stop = stop ?? tripContext.stops.find(s => s.id === stopId) ?? {} as Stop
    const company: Company = tripContext.companies.find(company => company.id === stopData.companyId) ?? {} as Company

    const na = 'N/A'
    const failedRequest: SyncStatus | undefined = tripContext.syncStatus.find(x => x.id === stopId)
   
    const appointmentFormatted = (stopData && stopData.appointmentTime) && dateFormat(stopData.appointmentTime, 'mm/dd HH:MM');
    const isSupplierPickup: boolean = stopData != null && stopData.orderTypes?.map(type => type.replace(/\s+/g, ' ').trim())?.some(type => type === OrderType.VendorPickup) // removes double space
    const isStc: boolean = stopData != null && stopData.orderTypes?.some(item => item == OrderType.SubjectToCount) && stopData.stopEvent === 'LUL'
    const isDns: boolean = stopData != null && stopData.orderTypes?.some(item => item == OrderType.dropAndStay) && stopData.stopEvent === 'LUL'
    let tripId: number | undefined = getIntValue('tripId')

    const orders: Order[] = tripContext.orders.filter(lp => lp.stopId === stopId) ?? []
    
    const stopIsCompleteButFailedToSend: boolean = stopData?.stopStatus === StopStatus.Complete && failedRequest?.status == SyncingStatus.FailedToSync
    const stopIsStared: boolean = stopData?.stopStatus === StopStatus.Started
    const stopIsCompleteAndSent = stopData?.stopStatus === StopStatus.Complete && failedRequest == undefined
    const stopIsCompleteAndSyncing = stopData.stopStatus === StopStatus.Complete && failedRequest?.status == SyncingStatus.Syncing

    const icon: IconOptions | undefined = stopIsCompleteAndSyncing ? {icon: faSync, size: '2x', color: '#0066A4' }  : stopIsCompleteButFailedToSend ? { icon: faCloudXmark, size: '2x', color: '#EB5757' } : stopIsStared ? { icon: faSpinner, size: '2x', color: '#FF8552' } : stopIsCompleteAndSent ? { icon: faCheckCircle, size: '2x', color: '#76AB21' } : undefined

    const renderAppointmentTypeBadge = (): React.ReactNode => {
        let badge: React.ReactNode = <></>
        if (isDns) {
            badge = <div className={'card-badge'}>DNS</div>
        } 
        else if(isStc) {
            badge = <div className={'card-badge'}>STC</div>
        }
        else if(isSupplierPickup) {
            badge = <div className={'card-badge'}>LLD</div>
        }
        return badge  
    }

    const renderTemperatures = (): ReactNode => {
        let temps: string | undefined = stopData.orderTemperatures?.join(', ').replace(/, (?=[^,]*$)/, ' & ').trim()
        return <div className={'appointment-card-text'}>{temps?.length > 0 ? temps : na}</div>
    }

    const renderOrderTypes = (): ReactNode => {
        let orderTypes = stopData.orderTypes?.map(item => <div key={item}>{item}</div>) ?? []
        return <div className={'appointment-card-multiline'}>{orderTypes.length > 0 ? orderTypes : na}</div>
    }

    const onClickStart = () => {
        if (stopData.stopStatus === StopStatus.Started) {
            isSupplierPickup ?
                navigateTo(`supplier-pickup?tripId=${tripId}&stopId=${stopId}&readOnly=false`)
                :
                navigateTo(`stop-overview?tripId=${tripId}&stopId=${stopData.id}&dry=${true}&frozen=${true}&ref=${true}`)
        } else {
            navigateTo(`temp-integrity?tripId=${tripId}&stopId=${stopData.id}`)
        }
    }

    const onClickView = () => {
        if (isSupplierPickup) {
            navigateTo(`supplier-pickup?tripId=${tripId}&stopId=${stopId}&readOnly=true`)
        }
        else {
            const allTemps: Temperature[] = orders.map(x => x.temperature).filter((v, i, a) => a.indexOf(v) === i)
            if (allTemps.length === 1) {
                navigateTo(`recap?tripId=${tripId}&stopId=${stopId}&readOnly=true&dry=true&frozen=true&ref=true`)
            } else {
                setIsRecapPopupVisible(true)
            }
        }
    }

    const onSyncIconClick = async (): Promise<void> => {
        if (await syncRequests()) {
          notify(`Successfully Synced!`, 'success', 5000)
        } else {
          notify(`There was a problem syncing some of your changes. Ensure you have an internet connection and try again`, 'info', 5000)
        }
      }

    return (
        <div className={'card-material'}>
            <div onClick={() => setDetailVisible(!detailVisible)}>
                <HeadingItem
                    text={company.id ? (company.name ?? 'Unknown Company') : 'Loading...'}
                    rightHandIcon={icon ?? undefined}
                    leftHandRender={() => renderAppointmentTypeBadge()}
                />
                {stopData?.stopStatus !== StopStatus.Complete &&
                    <div className={'appointment-card-row'}>
                        <div className={'appointment-card-leftcolumn'}>
                            <FontAwesomeIcon icon={faClock as IconProp} size='2x' color='#0066A4' />
                        </div>
                        <div className={'appointment-card-text'}> {appointmentFormatted} </div>
                    </div>
                }
            </div>
            <CSSTransition appear={detailVisible} in={detailVisible} classNames={'card-details'} timeout={500} nodeRef={cardContentRef}>
                <div ref={cardContentRef} className={'card-details'}>
                    {stopData?.stopStatus == StopStatus.Complete &&
                        <div className={'appointment-card-row'}>
                            <div className={'appointment-card-leftcolumn'}>
                                <FontAwesomeIcon icon={faClock as IconProp} size='2x' color='#0066A4' />
                            </div>
                            <div className={'appointment-card-text'}> {appointmentFormatted} </div>
                        </div>
                    }
                    <div className={'appointment-card-row'}>
                        <div className={'appointment-card-leftcolumn'}>
                            <FontAwesomeIcon icon={faMapMarkerAlt as IconProp} size='2x' color='#0066A4' />
                        </div>
                        <div className={'appointment-card-text'}> {company.city}, {company.state}</div>
                    </div>
                    <div className={'appointment-card-row'}>
                        <div className={'appointment-card-leftcolumn'}>
                            <FontAwesomeIcon icon={faDolly as IconProp} size='2x' color='#0066A4' />
                        </div>
                        <div className={'appointment-card-text'}> {stopData.stopEvent} </div>
                    </div>
                    <div className={'appointment-card-row'}>
                        <div className={'appointment-card-leftcolumn'}>
                            <FontAwesomeIcon icon={faThermometerHalf as IconProp} size='2x' color='#0066A4' />
                        </div>
                        {renderTemperatures()}
                    </div>
                    <div className={'appointment-card-row'}>
                        <div className={'appointment-card-leftcolumn'}>
                            <FontAwesomeIcon icon={faTruckLoading as IconProp} size='2x' color='#0066A4' />
                        </div>
                        {renderOrderTypes()}
                    </div>
                    <div className={'action-row'}>
                        <ActionButton icon={faInfoCircle} buttonColor={'#3498DB'} buttonText='More Info' buttonWidth={'100px'} onClick={() => setMoreDetailsVisible(!moredetailsVisible)} />
                        {stopData.stopStatus === StopStatus.Complete ?
                            <ActionButton icon={faClipboardCheck} buttonColor={'#82BC24'} buttonWidth={stopIsCompleteButFailedToSend ? '100px' : '120px'} buttonText={isSupplierPickup ? 'View Pickups' : 'View Recap'} onClick={onClickView} />
                            :
                            <Fragment>
                                {!isReadOnly &&
                                    <Fragment>
                                        {stopData.stopStatus === StopStatus.Started &&
                                            <ActionButton icon={faPencil} iconSize={'1x'} buttonText={'Edit Temp'} buttonColor={'#F2994A'} buttonWidth={'100px'} onClick={() => navigateTo(`temp-integrity?tripId=${tripId}&stopId=${stopData.id}`)} />
                                        }
                                        <ActionButton icon={faArrowRight} buttonColor={'#82BC24'} buttonWidth={'100px'} buttonText={stopData.stopStatus === StopStatus.Started ? 'Resume' : 'Start'} onClick={onClickStart} />
                                    </Fragment>
                                }
                            </Fragment>
                        }
                        {stopIsCompleteButFailedToSend &&
                             <ActionButton
                             icon={faSync}
                             buttonColor={'#EB5757'}
                             buttonText='Sync'
                             onClick={onSyncIconClick}
                             buttonWidth={'100px'}
                           />
                        }
                    </div>
                    <CSSTransition in={moredetailsVisible} classNames={'card-details'} timeout={1000} nodeRef={cardDetailsRef} >
                        <div className={'card-details'} ref={cardDetailsRef}>
                            {company && <Suspense fallback={'loading'}><AppointmentInfo stopId={stopId} stop={stopData} /></Suspense>}
                            {stopData.summary && <Suspense fallback={'loading'}><AppointmentSummary stop={stopData} /></Suspense>}
                        </div>
                    </CSSTransition>
                </div>
            </CSSTransition>
            <RecapPopup
                visible={isRecapPopupVisible}
                tripId={tripId}
                stopId={stopId}
                isNavFromStopOverview={false}
                onClickCancel={() => setIsRecapPopupVisible(false)} />
        </div>
    )
}